import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import { AppModel } from "../../../app/app-model";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { assert } from "../../../utils/utils";
import { AccessPoliciesItem } from "./access-policies";
import { EditAccessPolicyDialog } from "./edit-access-policy-dialog";

export const AccessPoliciesRoute = observer(
  function AccessPoliciesRoute(props: { app: AppModel }) {
    const { t } = useLingui();
    const admin = props.app.requireAdminShit();
    const model = admin.accessPolicies;
    assert(model, "Access policies model not initialized");

    return (
      <>
        <DataTable<AccessPoliciesItem>
          persistState
          editMode="dialog"
          itemKeyField="uuid"
          filterNoun={(count) =>
            t`${plural(count, { one: "policy", other: "policies" })}`
          }
          columns={[
            {
              header: <Trans>Username</Trans>,
              attr: "username",
              type: "string",
              sort: true,
              filter: {
                kind: "text",
                group: t`Users`,
                placeholder: t`Search…`,
              },
              sticky: true,
              initSort: "asc",
              cell: ({ item }) => <strong>{item.username}</strong>,
            },
            {
              header: <Trans>Role</Trans>,
              attr: "userRole",
              type: "string",
              filter: {
                kind: "multi",
                group: t`Users`,
                options: uniqueValueOptions<AccessPoliciesItem>("userRole"),
              },
              cell: ({ item }) => (
                <span className="text-xs text-muted-foreground">
                  {item.userRole}
                </span>
              ),
            },
            {
              header: <Trans>Monitoring active</Trans>,
              attr: "monitoring_active",
              type: "boolean",
              filter: {
                kind: "bool",
                group: t`Users`,
                predicate: (item) => !!item.monitoring_active,
                toggleLabel: t`Active only`,
              },
              width: 40,
            },
            {
              header: <Trans>Permissions</Trans>,
              attr: "permissions",
              width: 30,
              cell: ({ item }) => item.permissions.length,
            },
            {
              header: <Trans>Locations</Trans>,
              attr: "locations",
              width: 30,
              cell: ({ item }) => item.locations?.length,
            },
            {
              header: <Trans>Clinics</Trans>,
              attr: "numClinics",
              width: 30,
            },
            {
              header: <Trans>Physicians</Trans>,
              attr: "physicians",
              width: 30,
              cell: ({ item }) => item.physicians?.length,
            },
            {
              header: <Trans>Visible patients</Trans>,
              attr: "numVisiblePatients",
              width: 30,
            },
            {
              header: <Trans>Updated</Trans>,
              attr: "updated_at",
              type: "datetime",
              sort: true,
            },
          ]}
          model={model}
          renderEditDialog={({ item, onClose, onSave }) => (
            <EditAccessPolicyDialog
              item={item}
              admin={admin}
              onClose={onClose}
              onSave={onSave}
            />
          )}
          onEdit={(patch, item) => admin.updateAccessPolicy(item.uuid, patch)}
        />
      </>
    );
  },
);
